import { useEffect, useId, useRef, useState, useCallback } from 'react'
import { useCopy, useLocale } from '../../content'
import { useSede } from '../../sede'
import { Eyebrow } from '../layout/Eyebrow'
import { isPlaceholderUrl } from '../../../../sedi/placeholders'
import { METHOD_NOTE, SITE_REVIEWS } from '../../../../reviews/strings'
import {
  VERCELLI_MIN_LIVE,
  formatAverage,
  googleFigure,
  liveOrNull,
  marqueeLoop,
  mergeStrip,
  type StripCard,
} from '../../../../reviews/model'
import { usePublicReviews } from '../../../../reviews/useLiveReviews'
import { useGoogleRating } from '../../../../reviews/googleRating'

/* ── Review data ──────────────────────────────────────────────────── */
interface Review {
  name: string
  stars: number
  text: string
}

const REVIEWS: Review[] = [
  { name: 'Ezio R.', stars: 5, text: 'Ditta altamente specializzata. Lavoro di abbattimento eseguito in modo esemplare. Ottima organizzazione e perfetta pulizia a fine lavoro.' },
  { name: 'Germana S.', stars: 5, text: 'Servizio veloce, personale competente. Alla fine dei lavori pulizia del sito perfetta. Ottimo team.' },
  { name: 'Marina A.', stars: 5, text: 'Molto molto bravi, puntuali e ben organizzati e super ordinati.' },
  { name: 'Pier Andrea C.', stars: 5, text: 'Ottima Ditta: puntuali, precisi, super professionali e con preventivi pi\u00F9 che onesti. Altamente consigliata!' },
  { name: 'Andrea R.', stars: 5, text: 'Puntuale e preciso. Lavori sempre all\u2019altezza delle aspettative. Consigliatissimo!' },
  { name: 'Iose B.', stars: 5, text: 'Da diversi anni faccio riferimento a loro per potatura e sistemazione giardino: lavoro valido, preciso e completo.' },
  { name: 'Teresa C.', stars: 5, text: 'Ottimo lavoro ragazzi! Bravo Andrea, preciso e professionale come sempre.' },
  { name: 'Lorella S.', stars: 5, text: 'Molto professionali, educati e gentili.' },
  { name: 'Lodovico B.', stars: 5, text: 'Ottima squadra, seri, competenti, veloci e molto attrezzati.' },
  { name: 'Franco G.', stars: 5, text: 'Professionalit\u00E0, disponibilit\u00E0, competenza e gentilezza. Cosa volere di pi\u00F9? Grazie Andrea e collaboratori.' },
  { name: 'Giuseppe F.', stars: 4, text: 'Intervento tempestivo e veloce!' },
]

/* ── Laurel wreath (corona d'alloro) — SVG wraps the number ──────── */
/**
 * The wreath is inline (one <symbol>, three <use>) rather than an <img> of
 * /media/verde-vercelli/laurel.svg: as a standalone image URL on the home it
 * was the picture Google chose for the page in search results. Paths are
 * that file's, verbatim.
 */
const LAUREL_ID = 'vc2-laurel'

function LaurelDefs() {
  return (
    <svg width="0" height="0" className="absolute" aria-hidden="true" focusable="false">
      <symbol id={LAUREL_ID} viewBox="0 0 511.982 511.982">
        <g fill="#8AC054">
          <path d="M28.202,108.105c78.263,32.624,51.061,91.418,51.061,91.418C13.249,204.712,28.202,108.105,28.202,108.105z" />
          <path d="M138.745,135.682c18.593,96.09-55.295,107.481-55.295,107.481C36.31,182.993,138.745,135.682,138.745,135.682z" />
          <path d="M0,227.79c95.388-7.859,98.605,65.17,98.605,65.17C35.108,332.396,0,227.79,0,227.79z" />
          <path d="M136.995,234.21c63.357,80.044,0.406,126.059,0.406,126.059C64.856,327.192,136.995,234.21,136.995,234.21z" />
          <path d="M21.999,340.848c109.402-42.029,138.276,42.732,138.276,42.732C99.199,451.751,21.999,340.848,21.999,340.848z" />
          <path d="M176.525,316.521c90.872,46.249,52.92,114.277,52.92,114.277C149.807,431.016,176.525,316.521,176.525,316.521z" />
        </g>
        <path
          fill="#79AA41"
          d="M353.487,474.266c-0.578,0-1.172-0.047-1.781-0.142c-120.917-20.296-204.961-68.138-249.788-142.183c-14.327-23.687-24.53-50.03-30.296-78.31c-4.594-22.515-6.406-46.311-5.391-70.701c1.734-41.499,11.016-70.294,11.406-71.498c1.828-5.609,7.859-8.656,13.453-6.828c5.593,1.828,8.655,7.844,6.827,13.437l0,0c-0.094,0.281-8.874,27.749-10.405,66.529c-1.375,35.358,3.188,87.747,32.765,136.511c41.498,68.388,120.559,112.809,234.96,131.995c5.813,0.984,9.734,6.484,8.767,12.281C363.128,470.579,358.613,474.266,353.487,474.266z"
        />
        <g fill="#9ED36A">
          <path d="M428.548,243.163c12.187-15.562,14.374-30.25,10.921-43.483c58.232-1.75,44.327-91.575,44.327-91.575c-47.67,19.858-56.217,49.451-55.607,69.544c-19.609-25.656-54.936-41.967-54.936-41.967C354.659,231.773,428.548,243.163,428.548,243.163z" />
          <path d="M413.392,292.96c10.076,6.265,19.437,8.891,28.046,8.891c45.701,0,70.544-74.061,70.544-74.061c-4.859-0.407-9.5-0.594-13.905-0.594C416.283,227.195,413.392,292.96,413.392,292.96z" />
          <path d="M402.611,334.208c18.374-40.733-27.608-99.997-27.608-99.997c-51.843,65.499-19.108,108.215-5.5,121.574c-13.016,13.797-17.78,27.796-17.78,27.796c14.358,16.016,29.607,22.156,44.545,22.156c48.576,0,93.747-64.889,93.747-64.889c-21.75-8.343-40.312-11.688-56.092-11.688C421.955,329.161,411.564,331.083,402.611,334.208z" />
          <path d="M279.771,430.799c79.544-6.75,42.843-118.715,42.843-118.715C235.929,366.206,279.771,430.799,279.771,430.799z" />
        </g>
        <path
          fill="#8AC054"
          d="M158.51,474.266c-5.109,0-9.64-3.688-10.515-8.906c-0.969-5.797,2.953-11.297,8.766-12.281c114.511-19.218,193.618-63.686,235.069-132.183c29.64-48.968,34.093-101.56,32.625-137.074c-1.61-38.593-10.281-65.498-10.375-65.764c-1.828-5.594,1.234-11.625,6.827-13.453c5.594-1.828,11.625,1.219,13.452,6.828c0.391,1.204,9.672,29.999,11.406,71.498c1.016,24.39-0.797,48.186-5.39,70.701c-5.767,28.279-15.969,54.639-30.296,78.31c-44.825,74.045-128.87,121.887-249.788,142.183C159.681,474.22,159.088,474.266,158.51,474.266z"
        />
        <path fill="#9ED36A" d="M426.845,122.402c34.765-52.592-52.592-84.685-52.592-84.685C365.331,117.95,426.845,122.402,426.845,122.402z" />
        <path fill="#8AC054" d="M137.745,37.718c8.922,80.232-52.592,84.685-52.592,84.685C50.388,69.81,137.745,37.718,137.745,37.718z" />
      </symbol>
    </svg>
  )
}

function LaurelStat({
  children,
  className = '',
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div
      className={`relative mx-auto flex items-center justify-center ${className}`}
    >
      <svg
        aria-hidden="true"
        focusable="false"
        className="absolute inset-0 w-full h-full opacity-20"
        style={{ filter: 'sepia(1) saturate(0.5) hue-rotate(10deg) brightness(0.8)' }}
      >
        <use href={`#${LAUREL_ID}`} />
      </svg>
      <div className="relative z-10">{children}</div>
    </div>
  )
}

/* ── Stars row ────────────────────────────────────────────────────── */
function Stars({ count }: { count: number }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }, (_, i) => (
        <svg
          key={i}
          width="14"
          height="14"
          viewBox="0 0 20 20"
          className={i < count ? 'text-brand-brass-bright' : 'text-vc2-ink/15'}
        >
          <path
            d="M10 1.5l2.47 5.01L18 7.27l-4 3.9.94 5.5L10 14.15l-4.94 2.52.94-5.5-4-3.9 5.53-.76L10 1.5z"
            fill="currentColor"
          />
        </svg>
      ))}
    </div>
  )
}

/* ── Animated counter hook ────────────────────────────────────────── */
function useCounter(target: number, duration: number, trigger: boolean) {
  const [value, setValue] = useState(0)

  useEffect(() => {
    if (!trigger) return
    let start: number | null = null
    let raf: number

    const step = (ts: number) => {
      if (!start) start = ts
      const progress = Math.min((ts - start) / duration, 1)
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3)
      setValue(Math.round(eased * target))
      if (progress < 1) raf = requestAnimationFrame(step)
    }

    raf = requestAnimationFrame(step)
    return () => cancelAnimationFrame(raf)
  }, [target, duration, trigger])

  return value
}

/* ── Section ──────────────────────────────────────────────────────── */
export function Numeri() {
  const copy = useCopy()
  const sectionRef = useRef<HTMLElement>(null)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const el = sectionRef.current
    if (!el) return
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); obs.disconnect() } },
      { threshold: 0.2 },
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [])

  const anni = useCounter(15, 1500, isVisible)
  const progetti = useCounter(500, 2000, isVisible)

  // Live data (Recensioni, spec §12.3), all fetched after mount, so the
  // prerender and the hydration carry none of it:
  // - our verified reviews join the strip ahead of the copied Google ones,
  //   from the first one (VERCELLI_MIN_LIVE);
  // - the third laurel shows Google's live rating (Places API, never cached),
  //   asked once the band is on screen; without it, label and link only;
  // - the Google link is the sede's profile from the backend, else the config's.
  const sede = useSede()
  const locale = useLocale()
  const t = SITE_REVIEWS[locale]
  const data = usePublicReviews(sede.sedeId)
  const verified = liveOrNull(data, VERCELLI_MIN_LIVE)
  const figure = googleFigure(useGoogleRating(data?.google_place_id, isVisible), locale)
  const configGoogle = sede.socials?.google && !isPlaceholderUrl(sede.socials.google) ? sede.socials.google : ''
  const profileUrl = data?.google_profile_url || configGoogle || GOOGLE_FALLBACK
  const cards = mergeStrip(verified?.reviews ?? [], GOOGLE_CARDS, locale)
  const [noteOpen, setNoteOpen] = useState(false)
  const noteId = useId()

  const googleLaurel = (
    <a
      href={profileUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="block text-center group cursor-pointer"
    >
      <LaurelStat className="w-[72px] h-[72px] sm:w-[140px] sm:h-[140px] lg:w-[165px] lg:h-[165px] mt-4 sm:mt-6 lg:mt-8">
        {figure && (
          <div
            className="font-display text-vc2-accent text-[18px] sm:text-[34px] lg:text-[42px] leading-none font-bold"
            style={{ letterSpacing: '0.01em' }}
          >
            <span aria-hidden="true">{figure.value}</span>
            <span className="sr-only">{t.googleRatingSr(figure.value, figure.count)}</span>
          </div>
        )}
      </LaurelStat>
      <div className="vc2-kicker text-[0.8rem] sm:text-[0.95rem] text-vc2-ink-soft mb-1 mt-2">
        {copy.numeri.reviews.label}
      </div>
      {/* The stars row keeps its height without a rating: nothing else moves when it arrives. */}
      <div className="flex justify-center mb-1 min-h-[14px]" aria-hidden="true">
        {figure && <Stars count={figure.stars} />}
      </div>
      <div className="vc2-kicker text-[0.8rem] sm:text-[0.95rem] text-vc2-ink-soft group-hover:text-vc2-accent transition-colors">
        {copy.numeri.reviews.cta} &rarr;
      </div>
    </a>
  )

  return (
    <section
      ref={sectionRef}
      id="numeri"
      className="relative bg-vc2-paper text-vc2-ink py-20 sm:py-28 overflow-hidden"
    >
      <LaurelDefs />

      {/* Bottom rule */}
      <div className="absolute left-0 right-0 bottom-0 h-px bg-vc2-hairline" aria-hidden="true" />

      <div className="relative max-w-6xl mx-auto px-5 sm:px-8">
        {/* ── Eyebrow ──────────────────────────────────── */}
        <div className="text-center mb-12 sm:mb-16">
          <Eyebrow className="vc2-kicker text-vc2-accent text-center mb-5">{copy.numeri.eyebrow}</Eyebrow>
          <h2 className="vc2-display text-vc2-ink text-4xl sm:text-5xl leading-tight">
            {copy.numeri.title}
          </h2>
        </div>
      </div>

      {/* ── Reviews marquee (full-bleed, between title and stats) ── */}
      <ReviewMarquee cards={cards} />

      <div className="relative max-w-6xl mx-auto px-5 sm:px-8">
        {/* ── Stats row ────────────────────────────────── */}
        <div className="grid grid-cols-3 gap-4 sm:gap-12 lg:gap-16 mt-14 sm:mt-20">
          {/* Anni */}
          <div className="text-center">
            <LaurelStat className="w-[72px] h-[72px] sm:w-[140px] sm:h-[140px] lg:w-[165px] lg:h-[165px] mt-4 sm:mt-6 lg:mt-8">
              <div
                className="font-display text-vc2-accent text-[18px] sm:text-[34px] lg:text-[42px] leading-none font-bold"
                style={{ letterSpacing: '0.01em' }}
              >
                {isVisible ? `${anni}+` : '0'}
              </div>
            </LaurelStat>
            <div className="vc2-kicker text-[0.8rem] sm:text-[0.95rem] text-vc2-ink-soft mb-1 mt-3">
              {copy.numeri.years.label}
            </div>
            <div className="text-[10px] sm:text-xs text-vc2-ink-soft italic font-display" style={{ letterSpacing: '0.02em' }}>
              {copy.numeri.years.since}
            </div>
          </div>

          {/* Progetti */}
          <div className="text-center">
            <LaurelStat className="w-[100px] h-[100px] sm:w-[210px] sm:h-[210px] lg:w-[260px] lg:h-[260px]">
              <div
                className="font-display text-vc2-accent text-[22px] sm:text-[43px] lg:text-[54px] leading-none font-bold"
                style={{ letterSpacing: '0.01em' }}
              >
                {isVisible ? `${progetti}+` : '0'}
              </div>
            </LaurelStat>
            <div className="vc2-kicker text-[0.8rem] sm:text-[0.95rem] text-vc2-ink-soft mt-3">
              {copy.numeri.projects.label}
            </div>
          </div>

          {/* Google Reviews (live rating), then a slot for the verified-reviews line and the note */}
          <div className="text-center">
            {googleLaurel}
            {/* Reserved from the first paint (and in the prerender), empty until the public
                list says there are verified reviews: the stats row never grows after paint. */}
            <div className="mt-3 min-h-[92px] sm:min-h-[64px]">
              {verified && (
                <>
                  <div className="text-[11px] sm:text-[13px] leading-snug text-vc2-ink-soft">
                    {t.verifiedLine(verified.count, verified.average !== null ? formatAverage(verified.average, locale) : null)}
                  </div>
                  <button
                    type="button"
                    aria-expanded={noteOpen}
                    aria-controls={noteId}
                    onClick={() => setNoteOpen((o) => !o)}
                    className="vc2-kicker text-[0.8rem] sm:text-[0.95rem] text-vc2-ink-soft hover:text-vc2-accent transition-colors underline underline-offset-4 decoration-vc2-ink/30"
                  >
                    {t.howShort}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* «Come raccogliamo le recensioni» — opened from the line under the Google laurel, full width under the stats. */}
        {verified && noteOpen && <MethodNotePanel id={noteId} />}
      </div>
    </section>
  )
}

/** Fallback for the Google link when neither the backend nor the sede config has one. */
const GOOGLE_FALLBACK = 'https://share.google/mA5JcEXJv0lNx2yyF'

/** The copied Google reviews, in the strip's card shape. */
const GOOGLE_CARDS = REVIEWS.map((r) => ({ name: r.name, rating: r.stars, text: r.text }))

/* ── «Come raccogliamo le recensioni» ─────────────────────────────── */

function MethodNotePanel({ id }: { id: string }) {
  const note = METHOD_NOTE[useLocale()]
  return (
    <div id={id} className="mx-auto mt-10 max-w-2xl rounded-xl border border-vc2-hairline bg-vc2-ink/[0.04] p-5 sm:p-6 text-left">
      <h3 className="vc2-display text-lg text-vc2-ink">{note.title}</h3>
      <ul className="mt-3 list-disc space-y-2 pl-5 text-[14px] leading-relaxed text-vc2-ink-soft">
        {note.points.map((p) => (
          <li key={p}>{p}</li>
        ))}
      </ul>
    </div>
  )
}

/* ── Review card ──────────────────────────────────────────────────── */

/** Above this many characters the three-line clamp may hide text: the card offers «Leggi tutto». */
const CLAMP_CHARS = 120

function ReviewCard({
  card,
  copyOfAnother,
  onReadMore,
}: {
  card: StripCard
  /** A loop copy: hidden from assistive tech, its button kept out of the tab order. */
  copyOfAnother: boolean
  onReadMore: (c: StripCard) => void
}) {
  const t = SITE_REVIEWS[useLocale()]
  const tag = card.source === 'verified' ? t.tagVerified : t.tagGoogle
  return (
    <div
      className="flex flex-col flex-shrink-0 w-[280px] sm:w-[300px] rounded-xl p-5
                 bg-vc2-ink/[0.06] border border-vc2-hairline"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <span className="vc2-display block truncate text-sm text-vc2-ink">{card.name}</span>
          <span className="block truncate text-[11px] text-vc2-ink-soft">
            {[tag, card.meta].filter(Boolean).join(' · ')}
          </span>
        </div>
        <span className="shrink-0 pt-0.5">
          <Stars count={card.rating} />
          <span className="sr-only">{t.stars(card.rating)}</span>
        </span>
      </div>
      {card.text && (
        <p lang="it" className="text-vc2-ink-soft text-[13px] leading-relaxed line-clamp-3">
          {card.text}
        </p>
      )}
      {card.text.length > CLAMP_CHARS && (
        <button
          type="button"
          tabIndex={copyOfAnother ? -1 : undefined}
          aria-label={t.readMoreOf(card.name)}
          aria-haspopup="dialog"
          onClick={() => onReadMore(card)}
          className="mt-2 self-start py-1 text-[13px] text-vc2-accent underline underline-offset-4"
        >
          {t.readMore}
        </button>
      )}
    </div>
  )
}

/**
 * The full text of one review, in a native modal dialog (focus trap, Esc,
 * focus back to the button). Mounted only while open, so the prerender
 * carries no dialog markup.
 */
function FullReviewDialog({ card, onClose }: { card: StripCard; onClose: () => void }) {
  const t = SITE_REVIEWS[useLocale()]
  const ref = useRef<HTMLDialogElement>(null)
  const titleId = useId()
  useEffect(() => {
    const d = ref.current
    if (d && !d.open) d.showModal()
  }, [])
  // Closing goes through the element, so the browser restores focus before the dialog unmounts.
  const close = () => ref.current?.close()
  const tag = card.source === 'verified' ? t.tagVerified : t.tagGoogle
  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      onClose={onClose}
      onClick={(e) => {
        if (e.target === e.currentTarget) close() // the backdrop
      }}
      className="w-[min(36rem,calc(100vw-2rem))] max-h-[85vh] overflow-auto rounded-xl border border-vc2-hairline bg-vc2-paper p-0 text-vc2-ink backdrop:bg-black/50"
    >
      <div className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 id={titleId} className="vc2-display text-lg text-vc2-ink">{card.name}</h3>
            <p className="text-[12px] text-vc2-ink-soft">{[tag, card.meta].filter(Boolean).join(' · ')}</p>
          </div>
          <span className="shrink-0 pt-1">
            <Stars count={card.rating} />
            <span className="sr-only">{t.stars(card.rating)}</span>
          </span>
        </div>
        <p lang="it" className="mt-4 whitespace-pre-line text-[15px] leading-relaxed text-vc2-ink-soft">
          {card.text}
        </p>
        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={close}
            className="min-h-[44px] rounded-full border border-vc2-hairline px-5 text-sm text-vc2-ink hover:bg-vc2-ink/5"
          >
            {t.close}
          </button>
        </div>
      </div>
    </dialog>
  )
}

/* ── Infinite marquee strip ───────────────────────────────────────── */

/**
 * One strip: verified reviews first, then the copied Google ones (model.ts
 * `mergeStrip`). The motion stops while the pointer or the keyboard focus is
 * on it, and while a full review is open.
 */
function ReviewMarquee({ cards }: { cards: readonly StripCard[] }) {
  const copy = useCopy()
  const stripRef = useRef<HTMLDivElement>(null)
  const [isPaused, setIsPaused] = useState(false)
  const [reading, setReading] = useState<StripCard | null>(null)

  // Pause on hover/touch/focus
  const pause = useCallback(() => setIsPaused(true), [])
  const resume = useCallback(() => setIsPaused(false), [])
  const closeReading = useCallback(() => setReading(null), [])
  const loop = marqueeLoop(cards)

  return (
    <>
      {/* `marquee-viewport`: under prefers-reduced-motion the global CSS
          stops the marquee and makes this container scrollable so the
          reviews stay reachable. */}
      <div
        role="region"
        aria-label={copy.a11y.reviews}
        className="marquee-viewport overflow-hidden"
        onMouseEnter={pause}
        onMouseLeave={resume}
        onTouchStart={pause}
        onTouchEnd={resume}
        onFocus={pause}
        onBlur={resume}
      >
        <div
          ref={stripRef}
          className="flex w-max gap-4 animate-marquee-reviews"
          style={{ animationPlayState: isPaused || reading ? 'paused' : 'running' }}
        >
          {/* Two copies for seamless loop */}
          {[0, 1].map((half) => (
            <div key={half} className="flex gap-4" aria-hidden={half === 1 || undefined}>
              {loop.map(({ review, repeat }, i) => (
                <div key={`${half}-${i}`} className="flex" aria-hidden={(half === 0 && repeat) || undefined}>
                  <ReviewCard card={review} copyOfAnother={half === 1 || repeat} onReadMore={setReading} />
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
      {reading && <FullReviewDialog card={reading} onClose={closeReading} />}
    </>
  )
}
